// Holiday snow stake: the drift rises with the calendar. Each mark is a release date; the snow
// reaches it on that day and the mystery Dupe is revealed. Between marks the depth is interpolated,
// so the snow keeps creeping up day by day. Dates live on the element and on each mark.
class SSnowStake extends HTMLElement {
  connectedCallback() {
    const start = this.#parse(this.dataset.start);
    const max = Number(this.dataset.max || 30);
    const marks = [...this.querySelectorAll('[data-stake-mark]')]
      .map((mark) => ({ el: mark, inch: Number(mark.dataset.inch), date: this.#parse(mark.dataset.release) }))
      .filter((mark) => mark.date)
      .sort((a, b) => a.date - b.date);
    if (!start || marks.length === 0) return;

    const now = Date.now();
    const points = [{ inch: 0, date: start }, ...marks];
    let depth = 0;
    for (let i = 1; i < points.length; i += 1) {
      const prev = points[i - 1];
      const next = points[i];
      if (now >= next.date) { depth = next.inch; continue; }
      if (now > prev.date) depth = prev.inch + ((now - prev.date) / (next.date - prev.date)) * (next.inch - prev.inch);
      break;
    }

    let nextMark = null;
    marks.forEach((mark) => {
      const released = now >= mark.date;
      mark.el.toggleAttribute('data-released', released);
      if (!released && !nextMark) nextMark = mark;
    });
    nextMark?.el.setAttribute('data-next', '');

    const readout = this.querySelector('[data-stake-readout]');
    if (readout) readout.textContent = depth.toFixed(1);

    this.style.setProperty('--stake-level', String(Math.min(depth / max, 1)));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      this.setAttribute('data-filled', '');
      return;
    }
    const dialog = this.closest('dialog');
    if (dialog) {
      // In a pop-up, the snow fills every time it opens and drains when it closes, so it replays.
      const fill = () => requestAnimationFrame(() => requestAnimationFrame(() => this.setAttribute('data-filled', '')));
      this.observer = new MutationObserver(() => {
        if (dialog.open) fill();
        else this.removeAttribute('data-filled');
      });
      this.observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
      if (dialog.open) fill();
      return;
    }
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.setAttribute('data-filled', '');
      this.observer.disconnect();
    }, { threshold: 0.25 });
    this.observer.observe(this);
    // Never leave the stake empty if the observer misses it.
    setTimeout(() => this.setAttribute('data-filled', ''), 4000);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  #parse(value) {
    if (!value) return null;
    const time = Date.parse(`${value}T00:00:00`);
    return Number.isNaN(time) ? null : time;
  }
}

if (!customElements.get('s-snow-stake')) customElements.define('s-snow-stake', SSnowStake);
